import { defer, finalize, from, map, of, type Observable } from 'rxjs'
import { createAnimation, SPRING_BOUNCY, SPRING_GENTLE, SPRING_SMOOTH } from '@schmancy/utils/animation'
import { reducedMotion$ } from './reduced-motion'

/** Eight sparks from the centre, the first straight up, alternating a long and a short throw. */
const SPARKS = Array.from({ length: 8 }, (_, i) => ({
	angle: (i / 8) * Math.PI * 2 - Math.PI / 2,
	distance: i % 2 === 0 ? 132 : 92,
}))

/** An element (its centre) or an explicit viewport point the bloom and sparks emanate from. */
export type BurstOrigin = HTMLElement | { x: number; y: number }

export interface BurstOptions {
	/** Where the bloom and sparks rise from; the clip stays `host`'s own box regardless. Defaults to `host`'s own centre. */
	origin?: BurstOrigin
	/**
	 * Lifts and fades `host`'s own children on SPRING_GENTLE while the burst runs, so the
	 * reporter watches their content leave rather than sit behind a blank panel. Restored
	 * (via `Animation.cancel()`) once the burst completes; a no-op under `reducedMotion$`.
	 */
	lift?: boolean
}

const originCenter = (origin: BurstOrigin): { x: number; y: number } =>
	origin instanceof HTMLElement
		? (({ left, top, width, height }) => ({ x: left + width / 2, y: top + height / 2 }))(
				origin.getBoundingClientRect(),
			)
		: origin

/**
 * A one-shot celebratory burst rising from `options.origin` (default: the centre of `host`
 * itself): a soft bloom of the primary tone and eight 12px sparks alternating primary and
 * tertiary, flying out on SPRING_BOUNCY and lit until 60% of the flight, the bloom on
 * SPRING_SMOOTH — over inside 600 ms, clipped to `host`'s own box regardless of where the
 * origin sits, inert to the pointer, gone when it ends. Emits once and completes when the
 * last animation finishes (`options.lift`'s included), so a caller sequences what follows
 * (a close) after it. Under `reducedMotion$` nothing is drawn and it completes at once.
 *
 * `host` is the positioning box and the clip. A `static` host is made `relative` for the
 * burst's lifetime and restored after; unsubscribing early tears the burst down.
 */
export function burst(host: HTMLElement, options: BurstOptions = {}): Observable<void> {
	return defer(() => {
		if (reducedMotion$.value) return of(undefined)

		const layer = document.createElement('div')
		layer.setAttribute('aria-hidden', 'true')
		layer.className = 'absolute inset-0 overflow-hidden pointer-events-none'

		const bloom = document.createElement('span')
		bloom.className = 'absolute left-1/2 top-1/2 -ml-3 -mt-3 h-6 w-6 rounded-full bg-primary-default/30 blur-sm opacity-0'

		const sparks = SPARKS.map((_, i) => {
			const spark = document.createElement('span')
			spark.className = `absolute left-1/2 top-1/2 -ml-1.5 -mt-1.5 h-3 w-3 rounded-full opacity-0 ${i % 2 === 0 ? 'bg-primary-default' : 'bg-tertiary-default'}`
			return spark
		})
		layer.append(bloom, ...sparks)

		if (options.origin !== undefined) {
			const hostRect = host.getBoundingClientRect()
			const originPoint = originCenter(options.origin)
			const offsetX = originPoint.x - (hostRect.left + hostRect.width / 2)
			const offsetY = originPoint.y - (hostRect.top + hostRect.height / 2)
			for (const el of [bloom, ...sparks]) {
				el.style.left = `calc(50% + ${offsetX}px)`
				el.style.top = `calc(50% + ${offsetY}px)`
			}
		}

		const previousPosition = host.style.position
		const wasStatic = getComputedStyle(host).position === 'static'
		if (wasStatic) host.style.position = 'relative'

		const liftTargets = options.lift ? Array.from(host.children) as HTMLElement[] : []
		host.appendChild(layer)

		const grow = createAnimation(SPRING_SMOOTH, {
			from: { opacity: 0.9, transform: 'scale(1)' },
			to: { opacity: 0, transform: 'scale(12)' },
		})
		const animations = [
			bloom.animate([grow.keyframes[0] ?? {}, { opacity: 0.8, offset: 0.5 }, grow.keyframes[1] ?? {}], grow.options),
			...sparks.map((spark, i) => {
				const { angle, distance } = SPARKS[i]
				const fly = createAnimation(SPRING_BOUNCY, {
					from: { opacity: 1, transform: 'translate(0, 0) scale(1)' },
					to: {
						opacity: 0,
						transform: `translate(${Math.cos(angle) * distance}px, ${Math.sin(angle) * distance}px) scale(1)`,
					},
				})
				// A plateau keeps the spark lit through most of its flight; it dies
				// only at the end, never mid-air.
				return spark.animate([fly.keyframes[0] ?? {}, { opacity: 1, offset: 0.6 }, fly.keyframes[1] ?? {}], fly.options)
			}),
			...liftTargets.map(el => {
				const rise = createAnimation(SPRING_GENTLE, {
					from: { opacity: 1, transform: 'translateY(0)' },
					to: { opacity: 0.4, transform: 'translateY(-8px)' },
				})
				return el.animate(rise.keyframes, rise.options)
			}),
		]

		return from(Promise.all(animations.map(a => a.finished))).pipe(
			map((): void => undefined),
			finalize(() => {
				for (const a of animations) a.cancel()
				layer.remove()
				if (wasStatic) host.style.position = previousPosition
			}),
		)
	})
}
